import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, ArrowLeft, Loader2, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSupport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (user?.email !== 'nicoeconomakis@gmail.com') {
      navigate('/');
      return;
    }
    fetchThreads();
    const interval = setInterval(fetchThreads, 15000);
    return () => clearInterval(interval);
  }, [user, navigate]);

  const fetchThreads = async () => {
    try {
      const res = await axios.get(`${API}/admin/support/threads`, { withCredentials: true });
      setThreads(res.data.threads || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const fetchThread = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/support/thread/${userId}`, { withCredentials: true });
      setMessages(res.data.messages || []);
    } catch (err) {
      toast.error('Failed to load thread');
    }
  };

  const selectThread = (userId) => {
    setSelectedUserId(userId);
    fetchThread(userId);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchThread(userId), 5000);
  };

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendReply = async () => {
    if (!input.trim() || !selectedUserId || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/support/thread/${selectedUserId}/reply`, { message: input.trim() }, { withCredentials: true });
      setInput('');
      await fetchThread(selectedUserId);
      await fetchThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 flex" data-testid="admin-support-page">
      {/* Thread List */}
      <div className="w-80 border-r border-white/5 flex flex-col">
        <div className="px-4 py-3 border-b border-white/5 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-slate-400 hover:text-blue-400 h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-sm font-mono font-bold text-blue-400">Support Threads</h1>
          <span className="text-[10px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full font-mono">
            {threads.reduce((s, t) => s + (t.unread_admin || 0), 0)} unread
          </span>
        </div>

        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 ? (
            <p className="text-slate-600 text-xs text-center py-8 font-mono">No support threads</p>
          ) : (
            threads.map((t) => (
              <button
                key={t.user_id}
                onClick={() => selectThread(t.user_id)}
                className={`w-full text-left px-4 py-3 border-b border-white/3 hover:bg-white/3 transition-colors ${
                  selectedUserId === t.user_id ? 'bg-blue-500/10 border-l-2 border-l-blue-400' : ''
                }`}
                data-testid={`thread-${t.user_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <User className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                    <span className="text-xs font-medium truncate">{t.user_name || t.user_email}</span>
                  </div>
                  {(t.unread_admin || 0) > 0 && (
                    <span className="bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 flex-shrink-0">
                      {t.unread_admin}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 mt-0.5 truncate">
                  {t.messages?.[0]?.text || 'No messages'}
                </p>
                <p className="text-[9px] text-slate-600 mt-0.5">
                  {t.updated_at ? new Date(t.updated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {!selectedUserId ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="h-10 w-10 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-500 text-sm font-mono">Select a thread</p>
            </div>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-white/5 bg-slate-900/50">
              <p className="text-sm font-mono text-blue-400">
                {threads.find(t => t.user_id === selectedUserId)?.user_name || selectedUserId}
              </p>
              <p className="text-[10px] text-slate-500">
                {threads.find(t => t.user_id === selectedUserId)?.user_email}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] px-3 py-2 rounded-lg text-xs ${
                    msg.sender === 'admin'
                      ? 'bg-blue-500/15 text-blue-200 border border-blue-500/20'
                      : 'bg-slate-800 text-slate-200 border border-white/5'
                  }`}>
                    <p className="text-[10px] font-mono text-slate-500 mb-1">
                      {msg.sender_name} — {new Date(msg.sent_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="whitespace-pre-wrap">{msg.text}</p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-4 border-t border-white/5">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendReply()}
                  placeholder="Reply..."
                  className="flex-1 bg-slate-900/80 border border-white/10 text-slate-200 text-sm rounded-lg px-3 py-2 focus:border-blue-400/50 focus:outline-none placeholder:text-slate-600"
                  disabled={sending}
                  data-testid="admin-reply-input"
                />
                <Button
                  onClick={sendReply}
                  disabled={sending || !input.trim()}
                  className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30"
                  data-testid="admin-reply-send"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
